import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Search, X, ArrowLeft, Send, RefreshCw, Loader2, CheckCircle, Clock,
  AlertTriangle, User, Shield, Filter, ChevronRight, MessageSquare, Eye, Plus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLES = {
  low: 'bg-gray-100 text-gray-700', medium: 'bg-blue-100 text-blue-800',
  high: 'bg-amber-100 text-amber-800', urgent: 'bg-red-100 text-red-800',
};
const STATUS_STYLES = {
  open: 'bg-blue-100 text-blue-800', in_progress: 'bg-amber-100 text-amber-800',
  waiting_on_user: 'bg-purple-100 text-purple-800', resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};
const STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', waiting_on_user: 'Waiting',
  resolved: 'Resolved', closed: 'Closed',
};

export default function AdminHelpdesk({ adminEmail }) {
  const [view, setView] = useState('queue');
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [createForm, setCreateForm] = useState({ user_email: '', user_name: '', subject: '', message: '', category: 'general', priority: 'medium', admin_note: '' });

  useEffect(() => { loadStats(); loadTickets(); }, [filterStatus, filterPriority, filterCategory]);

  const loadStats = async () => {
    try {
      const res = await axios.get(`${API}/helpdesk/admin/stats`);
      setStats(res.data);
    } catch (e) {}
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterPriority) params.set('priority', filterPriority);
      if (filterCategory) params.set('category', filterCategory);
      if (searchTerm) params.set('search', searchTerm);
      const res = await axios.get(`${API}/helpdesk/admin/tickets?${params}`);
      setTickets(res.data.tickets || []);
    } catch (e) {}
    finally { setLoading(false); }
  };

  const openTicket = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/helpdesk/tickets/${ticketId}`);
      setSelectedTicket(res.data);
      setView('detail');
    } catch (e) {}
  };

  const handleReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/helpdesk/admin/tickets/${selectedTicket.ticket_id}/reply`, { message: replyText, is_internal: isInternal });
      setReplyText('');
      setIsInternal(false);
      openTicket(selectedTicket.ticket_id);
      loadStats();
    } catch (e) { alert('Failed'); }
    finally { setSending(false); }
  };

  const updateTicket = async (field, value) => {
    if (!selectedTicket) return;
    try {
      await axios.put(`${API}/helpdesk/admin/tickets/${selectedTicket.ticket_id}`, { [field]: value });
      openTicket(selectedTicket.ticket_id);
      loadTickets();
      loadStats();
    } catch (e) {}
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    try {
      const data = { ticket_ids: selectedIds, action };
      if (action === 'assign') data.assigned_to = adminEmail;
      await axios.post(`${API}/helpdesk/admin/bulk-action`, data);
      setSelectedIds([]);
      loadTickets();
      loadStats();
    } catch (e) {}
  };

  const handleCreateTicket = async () => {
    if (!createForm.subject || !createForm.message) return;
    setSending(true);
    try {
      await axios.post(`${API}/helpdesk/admin/tickets`, createForm);
      setShowCreateTicket(false);
      setCreateForm({ user_email: '', user_name: '', subject: '', message: '', category: 'general', priority: 'medium', admin_note: '' });
      loadTickets();
      loadStats();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setSending(false); }
  };

  const timeSince = (dateStr) => {
    if (!dateStr) return '-';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  // --- TICKET DETAIL ---
  if (view === 'detail' && selectedTicket) {
    return (
      <div data-testid="helpdesk-detail">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => { setView('queue'); setSelectedTicket(null); loadTickets(); }} className="p-2 hover:bg-[#F4F2EC] rounded-lg"><ArrowLeft className="w-5 h-5 text-[#475569]" /></button>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg font-bold text-[#1B3A6B] truncate">{selectedTicket.subject}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-[#94A3B8]">{selectedTicket.ticket_id}</span>
              <span className="text-xs text-[#94A3B8]">· {selectedTicket.user_email}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar Controls */}
          <div className="space-y-3">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <label className="text-[10px] text-[#94A3B8] uppercase tracking-wider block mb-1">Status</label>
              <select value={selectedTicket.status} onChange={e => updateTicket('status', e.target.value)} className="w-full px-2 py-2 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ticket-status-select">
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_on_user">Waiting on User</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <label className="text-[10px] text-[#94A3B8] uppercase tracking-wider block mb-1">Priority</label>
              <select value={selectedTicket.priority} onChange={e => updateTicket('priority', e.target.value)} className="w-full px-2 py-2 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ticket-priority-select">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <label className="text-[10px] text-[#94A3B8] uppercase tracking-wider block mb-1">Category</label>
              <select value={selectedTicket.category} onChange={e => updateTicket('category', e.target.value)} className="w-full px-2 py-2 border border-[#E2DFD8] rounded-lg text-sm">
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="account">Account</option>
                <option value="feature_request">Feature Request</option>
                <option value="general">General</option>
              </select>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <label className="text-[10px] text-[#94A3B8] uppercase tracking-wider block mb-1">Assigned To</label>
              <button onClick={() => updateTicket('assigned_to', adminEmail)} className={`w-full px-2 py-2 text-sm rounded-lg text-left ${selectedTicket.assigned_to ? 'bg-[#F4F2EC] text-[#0F1A2A]' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`} data-testid="ticket-assign-btn">
                {selectedTicket.assigned_to || 'Assign to me'}
              </button>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm text-xs text-[#94A3B8] space-y-1">
              <div>Created: {timeSince(selectedTicket.created_at)} ago</div>
              <div>First response: {selectedTicket.first_response_at ? timeSince(selectedTicket.first_response_at) + ' ago' : 'None yet'}</div>
              {selectedTicket.resolved_at && <div>Resolved: {timeSince(selectedTicket.resolved_at)} ago</div>}
            </div>
          </div>

          {/* Conversation */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
                {(selectedTicket.messages || []).map(m => (
                  <div key={m.id} className={`flex ${m.sender_type === 'user' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[75%] rounded-xl px-4 py-3 ${
                      m.is_internal ? 'bg-amber-50 border border-amber-200' :
                      m.sender_type === 'user' ? 'bg-[#F4F2EC]' : 'bg-[#1B3A6B] text-white'
                    }`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        {m.sender_type === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        <span className={`text-[10px] font-medium ${m.is_internal ? 'text-amber-700' : m.sender_type === 'user' ? 'text-[#475569]' : 'text-white/70'}`}>
                          {m.sender_name || m.sender_type} {m.is_internal && '(Internal Note)'}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{m.message}</p>
                      <p className={`text-[10px] mt-1 ${m.is_internal ? 'text-amber-500' : m.sender_type === 'user' ? 'text-[#94A3B8]' : 'text-white/50'}`}>{new Date(m.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply */}
              <div className="border-t border-[#E2DFD8] p-4">
                <div className="flex items-center gap-2 mb-2">
                  <button onClick={() => setIsInternal(false)} className={`px-3 py-1 rounded-full text-xs font-medium ${!isInternal ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569]'}`} data-testid="reply-public">
                    <Send className="w-3 h-3 inline mr-1" />Reply
                  </button>
                  <button onClick={() => setIsInternal(true)} className={`px-3 py-1 rounded-full text-xs font-medium ${isInternal ? 'bg-amber-500 text-white' : 'bg-[#F4F2EC] text-[#475569]'}`} data-testid="reply-internal">
                    <Eye className="w-3 h-3 inline mr-1" />Internal Note
                  </button>
                </div>
                <div className="flex gap-2">
                  <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder={isInternal ? "Add internal note (not visible to user)..." : "Type your reply..."} rows={2} className={`flex-1 px-3 py-2.5 border rounded-lg text-sm resize-none ${isInternal ? 'border-amber-300 bg-amber-50' : 'border-[#E2DFD8]'}`} data-testid="admin-reply-input" />
                  <button onClick={handleReply} disabled={sending || !replyText.trim()} className={`px-4 rounded-lg font-medium text-sm text-white disabled:opacity-50 ${isInternal ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#1B3A6B] hover:bg-[#254B8A]'}`} data-testid="admin-reply-send">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- TICKET QUEUE ---
  return (
    <div data-testid="helpdesk-queue">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Support Helpdesk</h2>
        <button onClick={() => setShowCreateTicket(true)} className="flex items-center gap-1.5 px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium hover:bg-[#254B8A]" data-testid="admin-create-ticket-btn">
          <MessageSquare className="w-4 h-4" /> Create Ticket
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          {[
            { label: 'Open', value: stats.open, color: 'text-blue-600', filter: 'open' },
            { label: 'In Progress', value: stats.in_progress, color: 'text-amber-600', filter: 'in_progress' },
            { label: 'Waiting', value: stats.waiting_on_user, color: 'text-purple-600', filter: 'waiting_on_user' },
            { label: 'Unassigned', value: stats.unassigned, color: 'text-red-500', filter: '' },
            { label: 'Urgent', value: stats.urgent, color: 'text-red-600', filter: '' },
            { label: 'Total', value: stats.total, color: 'text-[#1B3A6B]', filter: '' },
          ].map((s, i) => (
            <button key={i} onClick={() => s.filter && setFilterStatus(s.filter)} className="bg-white p-3 rounded-xl shadow-sm text-left hover:shadow-md transition-all" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g,'-')}`}>
              <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
              <div className="text-[10px] text-[#94A3B8] mt-0.5">{s.label}</div>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadTickets()} placeholder="Search tickets..." className="w-full pl-9 pr-3 py-2 bg-white rounded-lg border border-[#E2DFD8] text-sm" data-testid="helpdesk-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 bg-white border border-[#E2DFD8] rounded-lg text-sm">
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="waiting_on_user">Waiting</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="px-3 py-2 bg-white border border-[#E2DFD8] rounded-lg text-sm">
          <option value="">All Priority</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-3 py-2 bg-white border border-[#E2DFD8] rounded-lg text-sm">
          <option value="">All Categories</option>
          <option value="billing">Billing</option>
          <option value="technical">Technical</option>
          <option value="account">Account</option>
          <option value="feature_request">Feature Request</option>
          <option value="general">General</option>
        </select>
        <button onClick={loadTickets} className="p-2 bg-white border border-[#E2DFD8] rounded-lg hover:bg-[#F4F2EC]"><RefreshCw className="w-4 h-4 text-[#475569]" /></button>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-[#1B3A6B] text-white px-4 py-2.5 rounded-lg">
          <span className="text-sm font-medium">{selectedIds.length} selected</span>
          <button onClick={() => handleBulkAction('close')} className="text-xs px-3 py-1 bg-white/20 rounded-full hover:bg-white/30">Close All</button>
          <button onClick={() => handleBulkAction('assign')} className="text-xs px-3 py-1 bg-white/20 rounded-full hover:bg-white/30">Assign to Me</button>
          <button onClick={() => setSelectedIds([])} className="ml-auto text-xs opacity-70 hover:opacity-100">Clear</button>
        </div>
      )}

      {/* Ticket List */}
      {loading ? <div className="text-center py-12 text-[#475569]">Loading...</div> : tickets.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-10 h-10 text-[#E2DFD8] mx-auto mb-3" />
          <p className="text-[#475569] font-medium">No tickets match your filters</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-[#F4F2EC]">
              <tr>
                <th className="px-3 py-3 w-8"><input type="checkbox" checked={selectedIds.length === tickets.length && tickets.length > 0} onChange={e => setSelectedIds(e.target.checked ? tickets.map(t => t.ticket_id) : [])} className="rounded" /></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569]">Ticket</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569]">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569]">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569]">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569]">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569]">Assigned</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#475569]">Age</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2DFD8]">
              {tickets.map(t => (
                <tr key={t.ticket_id} className={`hover:bg-[#F9F8F6] cursor-pointer ${selectedIds.includes(t.ticket_id) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.includes(t.ticket_id)} onChange={() => toggleSelect(t.ticket_id)} className="rounded" />
                  </td>
                  <td className="px-4 py-3" onClick={() => openTicket(t.ticket_id)}>
                    <p className="text-sm font-medium text-[#0F1A2A] truncate max-w-[250px]">{t.subject}</p>
                    <p className="text-[10px] text-[#94A3B8]">{t.ticket_id}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#475569]" onClick={() => openTicket(t.ticket_id)}>{t.user_name || t.user_email}</td>
                  <td className="px-4 py-3" onClick={() => openTicket(t.ticket_id)}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_STYLES[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                  </td>
                  <td className="px-4 py-3" onClick={() => openTicket(t.ticket_id)}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${PRIORITY_STYLES[t.priority]}`}>{t.priority}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#475569] capitalize" onClick={() => openTicket(t.ticket_id)}>{t.category?.replace('_', ' ')}</td>
                  <td className="px-4 py-3 text-xs text-[#475569]" onClick={() => openTicket(t.ticket_id)}>{t.assigned_to || <span className="text-red-400">Unassigned</span>}</td>
                  <td className="px-4 py-3 text-xs text-[#94A3B8]" onClick={() => openTicket(t.ticket_id)}>{timeSince(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Ticket Modal */}
      {showCreateTicket && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowCreateTicket(false)}>
          <div className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl" data-testid="admin-create-ticket-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#0F1A2A] text-lg">Create Ticket</h3>
              <button onClick={() => setShowCreateTicket(false)} className="p-1 hover:bg-[#F4F2EC] rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Contact Email</label>
                  <input value={createForm.user_email} onChange={e => setCreateForm(p => ({...p, user_email: e.target.value}))} placeholder="user@example.com" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ct-email" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Contact Name</label>
                  <input value={createForm.user_name} onChange={e => setCreateForm(p => ({...p, user_name: e.target.value}))} placeholder="John Smith" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ct-name" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Subject *</label>
                <input value={createForm.subject} onChange={e => setCreateForm(p => ({...p, subject: e.target.value}))} placeholder="Brief description" className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ct-subject" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Category</label>
                  <select value={createForm.category} onChange={e => setCreateForm(p => ({...p, category: e.target.value}))} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ct-category">
                    <option value="billing">Billing</option>
                    <option value="technical">Technical</option>
                    <option value="account">Account</option>
                    <option value="feature_request">Feature Request</option>
                    <option value="general">General</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Priority</label>
                  <select value={createForm.priority} onChange={e => setCreateForm(p => ({...p, priority: e.target.value}))} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ct-priority">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Message *</label>
                <textarea value={createForm.message} onChange={e => setCreateForm(p => ({...p, message: e.target.value}))} placeholder="Describe the issue..." rows={3} className="w-full px-3 py-2.5 border border-[#E2DFD8] rounded-lg text-sm" data-testid="ct-message" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Internal Note (optional)</label>
                <textarea value={createForm.admin_note} onChange={e => setCreateForm(p => ({...p, admin_note: e.target.value}))} placeholder="Private note for support team..." rows={2} className="w-full px-3 py-2.5 border border-amber-200 bg-amber-50 rounded-lg text-sm" data-testid="ct-note" />
              </div>
              <button onClick={handleCreateTicket} disabled={sending || !createForm.subject || !createForm.message} className="w-full py-2.5 bg-[#1B3A6B] text-white rounded-lg font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="ct-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
